import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Message, TypingIndicator, type MessageRole } from './Message';
import type { AgentResponseSegment } from '../ui-components';

export interface ChatMessage {
  id: string;
  role: MessageRole;
  content: string;
  segments?: AgentResponseSegment[];
}

interface ChatPanelProps {
  title: string;
  agentName: string;
  logoUrl: string | null;
  messages: ChatMessage[];
  sending: boolean;
  canSend: boolean;
  onSend: (message: string) => void;
  onClose: () => void;
}

export function ChatPanel({
  title,
  agentName,
  logoUrl,
  messages,
  sending,
  canSend,
  onSend,
  onClose,
}: ChatPanelProps): React.JSX.Element {
  const [draft, setDraft] = useState('');
  const bodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, sending]);

  // Refocus input when agent finishes responding
  useEffect(() => {
    if (!sending) inputRef.current?.focus();
  }, [sending]);

  // Grow the textarea to fit multi-line drafts. Resetting to 'auto' first
  // forces the browser to recompute scrollHeight against the current
  // content rather than the previous (possibly taller) height — otherwise
  // shrinking text would never shrink the box back down. The visual ceiling
  // before internal scrolling kicks in is CSS's max-height on .widget-input,
  // not duplicated here. useLayoutEffect (not useEffect) so the height is
  // corrected before paint — otherwise typing multi-line content briefly
  // flashes at the previous height on every keystroke.
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [draft]);

  function handleSubmit(): void {
    const text = draft.trim();
    if (!text || sending || !canSend) return;
    onSend(text);
    setDraft('');
    inputRef.current?.focus();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }

  return (
    <div className="widget-panel" role="dialog" aria-label={title}>
      <div className="widget-panel-header">
        <div className="widget-panel-header-logo">
          {logoUrl ? (
            <img src={logoUrl} alt="" />
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          )}
        </div>
        <div className="widget-panel-header-text">
          <div className="widget-panel-header-title">{title}</div>
          <div className="widget-panel-header-agent">{agentName}</div>
        </div>
        <button
          type="button"
          className="widget-panel-header-close"
          onClick={onClose}
          aria-label="Close chat"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="widget-panel-body" ref={bodyRef}>
        {messages.length === 0 && !sending ? (
          <div className="widget-panel-body-empty">Start the conversation below.</div>
        ) : (
          <div className="widget-messages">
            {messages.map((m) => (
              <Message key={m.id} role={m.role} content={m.content} segments={m.segments} />
            ))}
            {sending && <TypingIndicator />}
          </div>
        )}
      </div>

      <div className="widget-panel-footer">
        <textarea
          ref={inputRef}
          className="widget-input"
          rows={1}
          placeholder="Type your message"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={sending || !canSend}
        />
        <button
          type="button"
          className="widget-send-button"
          onClick={handleSubmit}
          disabled={!draft.trim() || sending || !canSend}
          aria-label="Send message"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
          </svg>
        </button>
      </div>
    </div>
  );
}
